import { useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import type { LucideIcon } from 'lucide-react'
import {
  BellRing,
  CalendarClock,
  CheckCircle2,
  Loader2,
  MoonStar,
  Pencil,
} from 'lucide-react'
import { refreshMessageReadModel } from '../lib/api'
import {
  splitCommitmentBoardBody,
  type CommitmentBoardTableRow,
  type CommitmentBoardSegment,
} from '../lib/commitmentBoard'
import {
  applyCommitmentAction,
  formatLocalDate,
  formatLocalDateTime,
  type CommitmentActionName,
  type CommitmentFollowupScope,
} from '../lib/commitmentActions'
import MarkdownBody from './MarkdownBody'
import SnoozePicker from './SnoozePicker'
import CommitmentFollowupScopeDialog from './CommitmentFollowupScopeDialog'
import { openExternalBrowser } from '../lib/externalBrowser'

type Props = {
  body: string
  chatId: string
  topicId: string | null
}

type AskMode = null | 'date' | 'datetime' | 'snooze'

type ButtonSpec = {
  key: string
  label: string
  Icon: LucideIcon
  tone: 'good' | 'warn' | 'soft' | 'neutral'
  ask: AskMode
  action: CommitmentActionName
  pickerTitle?: string
  doneLabel?: string
}

const BUTTONS: ButtonSpec[] = [
  { key: 'done', label: 'Done', Icon: CheckCircle2, tone: 'good', ask: null, action: 'done' },
  { key: 'snooze', label: 'Snooze', Icon: MoonStar, tone: 'warn', ask: 'snooze', action: 'snooze', pickerTitle: 'Snooze commitment' },
  { key: 'setdue', label: 'Set date', Icon: CalendarClock, tone: 'soft', ask: 'date', action: 'setdue', pickerTitle: 'Set promised date' },
  { key: 'setfu', label: 'Set follow-up', Icon: BellRing, tone: 'soft', ask: 'datetime', action: 'setfu', pickerTitle: 'Set follow-up date & time' },
]

const TONE_CLASSES: Record<ButtonSpec['tone'], string> = {
  good: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700/60 dark:hover:bg-emerald-900/40',
  warn: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700/60 dark:hover:bg-amber-900/40',
  soft: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-700/60 dark:hover:bg-sky-900/40',
  neutral: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-600/60 dark:hover:bg-slate-700/60',
}

export default function CommitmentBoardMessage({ body, chatId, topicId }: Props) {
  const segments = useMemo(() => splitCommitmentBoardBody(body), [body])

  return (
    <div className="space-y-1" data-testid="commitment-board-message">
      {segments.map((segment, index) => (
        segment.kind === 'commitment' ? (
          <CommitmentItem
            key={`${segment.id}-${index}`}
            segment={segment}
            chatId={chatId}
            topicId={topicId}
          />
        ) : segment.kind === 'commitment_table' ? (
          <CommitmentTable
            key={`table-${index}`}
            rows={segment.rows}
            chatId={chatId}
            topicId={topicId}
          />
        ) : (
          <MarkdownBody key={`md-${index}`} body={segment.body} />
        )
      ))}
    </div>
  )
}

function CommitmentTable({
  rows,
  chatId,
  topicId,
}: {
  rows: CommitmentBoardTableRow[]
  chatId: string
  topicId: string | null
}) {
  return (
    <div
      className="my-2 overflow-x-auto rounded-md border border-stone-300/80 bg-white/70 dark:border-stone-700/80 dark:bg-stone-950/25"
      data-testid="commitment-board-table"
    >
      <table className="min-w-[1180px] border-collapse text-left text-[11px] leading-snug">
        <thead>
          <tr className="border-b border-stone-300/80 bg-stone-100/90 text-[10px] uppercase text-stone-600 dark:border-stone-700/80 dark:bg-stone-900/75 dark:text-stone-300">
            <HeaderCell>ID</HeaderCell>
            <HeaderCell>Task</HeaderCell>
            <HeaderCell>Promised by</HeaderCell>
            <HeaderCell>Promised on</HeaderCell>
            <HeaderCell>Due date</HeaderCell>
            <HeaderCell>Remind</HeaderCell>
            <HeaderCell>Project</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Review</HeaderCell>
            <HeaderCell>Snooze</HeaderCell>
            <HeaderCell>Chases</HeaderCell>
            <HeaderCell>Notes</HeaderCell>
            <HeaderCell>Actions</HeaderCell>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-200/80 dark:divide-stone-800/80">
          {rows.map((row) => (
            <tr
              key={row.id}
              className="align-top odd:bg-white/55 even:bg-stone-50/55 dark:odd:bg-stone-950/10 dark:even:bg-stone-900/20"
              data-testid="commitment-board-table-row"
              data-commitment-id={row.id}
            >
              <BodyCell className="w-28">
                <code className="rounded bg-stone-200/80 px-1 py-0.5 font-mono text-[10px] text-stone-800 dark:bg-stone-800 dark:text-stone-100">
                  {row.id}
                </code>
              </BodyCell>
              <BodyCell className="w-64">
                <div className="line-clamp-4 max-w-[16rem] whitespace-normal font-semibold text-stone-950 dark:text-stone-50">
                  {row.task || 'Untitled task'}
                </div>
              </BodyCell>
              <BodyCell className="w-44">
                <div className="max-w-[11rem] whitespace-normal font-medium text-stone-900 dark:text-stone-100">
                  {row.promisedBy || 'Unknown'}
                </div>
                {row.email && row.email !== '—' && (
                  <div className="mt-0.5 max-w-[11rem] truncate text-[10px] text-stone-500 dark:text-stone-400">
                    {row.email}
                  </div>
                )}
              </BodyCell>
              <BodyCell className="w-24">{row.promisedOn || '—'}</BodyCell>
              <BodyCell className="w-40">
                <StatusText value={row.dueDate} />
              </BodyCell>
              <BodyCell className="w-44">
                <StatusText value={row.remind} />
              </BodyCell>
              <BodyCell className="w-28">{row.project || '—'}</BodyCell>
              <BodyCell className="w-24">
                <span className={statusBadgeClass(row.status)}>{row.status || 'open'}</span>
              </BodyCell>
              <BodyCell className="w-24">{row.review || '—'}</BodyCell>
              <BodyCell className="w-24">{row.snooze || '—'}</BodyCell>
              <BodyCell className="w-16">{row.chases || '0'}</BodyCell>
              <BodyCell className="w-72">
                <div className="line-clamp-3 max-w-[18rem] whitespace-normal text-stone-700 dark:text-stone-300">
                  {row.notes || '—'}
                </div>
              </BodyCell>
              <BodyCell className="w-60">
                {/* Keep the action buttons on a single non-wrapping row. The table
                    uses table-layout:auto inside an overflow-x-auto wrapper, so a
                    flex-wrap container collapses to one-button min-content on a
                    narrow phone — stacking all six icons vertically and inflating
                    the whole row's height. nowrap makes the column reserve the full
                    button-row width instead, keeping rows compact. */}
                <div className="flex flex-nowrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => void openExternalBrowser(`/commitments?commitment=${encodeURIComponent(row.id)}`)}
                    className={clsx(
                      'inline-grid h-8 w-8 place-items-center rounded-md border',
                      'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
                      'dark:border-blue-700/70 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-900/50',
                    )}
                    title={`Edit ${row.id}`}
                    aria-label={`Edit ${row.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <CommitmentActionRow
                    commitmentId={row.id}
                    chatId={chatId}
                    topicId={topicId}
                    compact
                  />
                </div>
              </BodyCell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function HeaderCell({ children }: { children: string }) {
  return <th className="whitespace-nowrap px-2 py-2 font-semibold tracking-normal">{children}</th>
}

function BodyCell({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <td className={clsx('px-2 py-2 text-stone-800 dark:text-stone-200', className)}>{children}</td>
}

function StatusText({ value }: { value: string }) {
  return (
    <span className={clsx(
      'line-clamp-2 whitespace-normal',
      /\boverdue\b/i.test(value) && 'font-semibold text-rose-700 dark:text-rose-300',
      /\btoday\b/i.test(value) && 'font-semibold text-amber-700 dark:text-amber-300',
      /\bNo (date|reminder)\b/i.test(value) && 'text-stone-500 dark:text-stone-400',
    )}>
      {value || '—'}
    </span>
  )
}

function statusBadgeClass(status: string) {
  const normalized = status.toLowerCase()
  return clsx(
    'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold',
    normalized === 'open' && 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-300',
    normalized === 'snoozed' && 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-300',
    normalized !== 'open' && normalized !== 'snoozed' && 'border-stone-200 bg-stone-50 text-stone-700 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300',
  )
}

function CommitmentItem({
  segment,
  chatId,
  topicId,
}: {
  segment: Extract<CommitmentBoardSegment, { kind: 'commitment' }>
  chatId: string
  topicId: string | null
}) {
  return (
    <div className="py-1.5" data-testid="commitment-board-item" data-commitment-id={segment.id}>
      <MarkdownBody body={segment.body} />
      <CommitmentActionRow
        commitmentId={segment.id}
        chatId={chatId}
        topicId={topicId}
      />
    </div>
  )
}

function CommitmentActionRow({
  commitmentId,
  chatId,
  topicId,
  compact = false,
}: {
  commitmentId: string
  chatId: string
  topicId: string | null
  compact?: boolean
}) {
  const qc = useQueryClient()
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [doneKey, setDoneKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pickerFor, setPickerFor] = useState<ButtonSpec | null>(null)

  async function submit(spec: ButtonSpec, value?: string, scope?: CommitmentFollowupScope) {
    setPendingKey(spec.key)
    setDoneKey(spec.key)
    setError(null)
    try {
      await applyCommitmentAction({ action: spec.action, commitmentId, value, scope }, qc)
      setDoneKey(spec.key)
      refreshMessageReadModel(qc, chatId, topicId)
      if (topicId) refreshMessageReadModel(qc, chatId, null)
      qc.invalidateQueries({ queryKey: ['topics', chatId] })
      qc.invalidateQueries({ queryKey: ['commitments-dashboard'] })
    } catch (e) {
      setDoneKey(null)
      setError(String(e).slice(0, 160))
    } finally {
      setPendingKey(null)
    }
  }

  function handleTap(spec: ButtonSpec) {
    if (pendingKey) return
    if (spec.ask) {
      setPickerFor(spec)
      return
    }
    void submit(spec)
  }

  function handlePicked(spec: ButtonSpec, unixSeconds: number, scope?: CommitmentFollowupScope) {
    setPickerFor(null)
    const d = new Date(unixSeconds * 1000)
    const value = spec.action === 'setfu' ? formatLocalDateTime(d) : formatLocalDate(d)
    void submit(spec, value, scope)
  }

  return (
    <>
      <div aria-busy={!!pendingKey} className={clsx(compact ? 'flex flex-nowrap items-center gap-1.5' : 'mt-1.5 flex flex-wrap gap-1.5')}>
        {BUTTONS.map((spec) => {
          const Icon = spec.Icon
          const isPending = pendingKey === spec.key
          const isDone = doneKey === spec.key
          return (
            <button
              key={spec.key}
              type="button"
              onClick={() => handleTap(spec)}
              disabled={!!pendingKey || isDone}
              className={clsx(
                compact
                  ? 'inline-grid h-8 w-8 place-items-center rounded-md border p-0'
                  : 'inline-flex min-h-8 items-center gap-1 rounded-full border px-2.5 py-1',
                'text-[11px] font-semibold leading-none transition-colors',
                'disabled:cursor-default disabled:opacity-70',
                isDone
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700'
                  : TONE_CLASSES[spec.tone],
              )}
              data-testid={`commitment-board-btn-${spec.key}`}
              title={`${spec.label} ${commitmentId}`}
            >
              {isPending && !isDone ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
              <span className={compact ? 'sr-only' : undefined}>
                {isDone ? (spec.doneLabel || 'Done') : spec.label}
              </span>
            </button>
          )
        })}
      </div>
      {error && (
        <div className="mt-1 text-[11px] text-rose-600 dark:text-rose-300 break-words">
          {error}
        </div>
      )}
      {pickerFor?.action === 'setfu' ? (
        <CommitmentFollowupScopeDialog
          onCancel={() => setPickerFor(null)}
          onPick={(unix, scope) => handlePicked(pickerFor, unix, scope)}
        />
      ) : pickerFor && (
        <SnoozePicker
          title={pickerFor.pickerTitle || 'Pick date'}
          footerNote={
            pickerFor.key === 'setdue'
              ? 'Sets the promised date this person owes you by.'
              : pickerFor.key === 'snooze'
                ? 'Hides this commitment until the chosen day, then it returns to the active list.'
              : 'When the tracker should bring this commitment back if still unresolved.'
          }
          onCancel={() => setPickerFor(null)}
          onPick={(unix) => handlePicked(pickerFor, unix)}
        />
      )}
    </>
  )
}
